import { useState, useEffect } from "react";
import { Download, Share, X } from "lucide-react";
import { isNative } from "../../lib/native";

const DISMISS_KEY = "flowbtp_pwa_install_dismissed";

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * Banniere d'installation PWA.
 * - Android / desktop Chrome : capture l'evenement `beforeinstallprompt`
 *   et propose un bouton "Installer".
 * - iOS : aucun evenement natif, on affiche la procedure manuelle Safari.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Dans l'app native (Capacitor), pas de bannière "ajouter à l'écran
    // d'accueil" : elle n'a de sens que sur le web/Safari.
    if (isNative() || isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    if (isIos()) {
      setIosHint(true);
      setVisible(true);
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-3 bottom-[76px] z-[60] md:inset-x-auto md:left-3 md:bottom-3 md:w-96">
      <div
        className="rounded-xl border bg-card shadow-lg p-3 flex items-center gap-3"
        style={{ borderColor: "var(--cf-border)" }}
      >
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: "var(--cf-blue)" }}
        >
          <Download className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ color: "var(--cf-ink)" }}>
            Installer KNOWROK
          </p>
          {iosHint ? (
            <p className="text-xs leading-snug" style={{ color: "var(--cf-muted)" }}>
              Appuyez sur <Share className="inline h-3 w-3 -mt-0.5" /> Partager,
              puis « Sur l'écran d'accueil ».
            </p>
          ) : (
            <p className="text-xs leading-snug" style={{ color: "var(--cf-muted)" }}>
              Accès rapide depuis l'écran d'accueil, en plein écran.
            </p>
          )}
        </div>
        {!iosHint && (
          <button
            type="button"
            onClick={install}
            data-testid="pwa-install-btn"
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-white shrink-0"
            style={{ background: "var(--cf-blue)" }}
          >
            Installer
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fermer"
          className="p-1 shrink-0"
          style={{ color: "var(--cf-muted)" }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
